/**
 * Map View Component
 *
 * Standalone map orchestrator.
 * Composes locations-drawer sidebar + map-canvas for standalone use.
 * Loads location data and initializes stores.
 * Uses Tailwind CSS for styling (light DOM rendering).
 *
 * @remarks
 * When embedding inside peach-local-pickup-view, use peach-map-canvas
 * directly instead — local-pickup-view owns the sidebar and skeleton removal.
 *
 * @element peach-map-view
 *
 * @fires view-ready - Emitted when component is initialized
 *
 * @example
 * ```html
 * <peach-map-view></peach-map-view>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import { cartStore } from "@/features/cart/stores/cart-store";
import { uiStore } from "@/stores/ui-store";
import { MobileView, type MobileViewType } from "@/constants/map";
import "./map-canvas";
import "./locations-drawer";
import "./map-filters";

@customElement("peach-map-view")
export class MapViewComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Google Maps API key
   */
  @property({ type: String })
  declare apiKey: string;

  /**
   * Current market ID (optional)
   */
  @property({ type: String })
  declare currentMarket: string;

  /**
   * Current tour ID (optional)
   */
  @property({ type: String })
  declare currentTour: string;

  /**
   * Whether to show filters panel
   */
  @property({ type: Boolean })
  declare showFilters: boolean;

  /**
   * Initial zoom level
   */
  @property({ type: Number })
  declare initialZoom: number;

  /**
   * Loading state
   */
  @state()
  declare private isLoading: boolean;

  /**
   * Error message
   */
  @state()
  declare private error: string | null;

  /**
   * Mobile view state (filters vs map vs list)
   */
  @state()
  declare private mobileView: MobileViewType;

  /**
   * Cart item count
   */
  @state()
  declare private cartItemCount: number;

  /**
   * Store unsubscribe functions
   */
  private unsubscribeMap?: () => void;
  private unsubscribeCart?: () => void;

  constructor() {
    super();
    this.apiKey = "";
    this.currentMarket = "";
    this.currentTour = "";
    this.showFilters = true;
    this.initialZoom = 6;
    this.isLoading = false;
    this.error = null;
    this.mobileView = MobileView.MAP;
    this.cartItemCount = 0;
  }

  /**
   * Subscribe to map store for loading, error states, and location data
   *
   * @remarks
   * Map store is now the single source of truth for location data.
   * Hides skeleton loader once locations are loaded.
   */
  private subscribeToMapStore() {
    this.unsubscribeMap = locationStore.subscribe(
      (state) => ({
        tours: state.tours,
        markets: state.markets,
        isLoading: state.isLoading,
        error: state.error,
      }),
      (selected) => {
        console.log("[MapView] MapStore changed:", {
          toursLength: selected.tours.length,
          marketsLength: selected.markets.length,
          total: selected.tours.length + selected.markets.length,
          isLoading: selected.isLoading,
        });

        this.isLoading = selected.isLoading;
        this.error = selected.error;

        // Hide skeleton once we have locations and are done loading
        const allLocations = locationStore.getState().getAllLocations();
        if (!selected.isLoading && allLocations.length > 0) {
          this.hideSkeletonLoader();
        }
      },
      {
        // Compare by array lengths and loading state to ensure we catch data updates
        equalityFn: (a, b) =>
          a.tours.length === b.tours.length &&
          a.markets.length === b.markets.length &&
          a.isLoading === b.isLoading &&
          a.error === b.error,
        // Fire immediately to sync any already-loaded data
        fireImmediately: true,
      },
    );
  }

  /**
   * Subscribe to cart store for item count
   */
  private subscribeToCartStore() {
    this.unsubscribeCart = cartStore.subscribe((state) => {
      this.cartItemCount = state.itemCount;
    });
  }

  /**
   * Set up window resize listener
   */
  private setupResizeListener() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);
  }

  /**
   * Initialize on connect
   *
   * @remarks
   * Data loading is handled by page-map.ts entrypoint.
   * This component just reacts to store state changes.
   */
  connectedCallback() {
    super.connectedCallback();

    console.log("[MapView] connectedCallback - setting up subscriptions");

    this.subscribeToMapStore();
    this.subscribeToCartStore();
    this.setupResizeListener();

    // Don't hide skeleton yet - wait for data to load
  }

  /**
   * Remove skeleton components when data is loaded
   *
   * @remarks
   * Skeleton components should remain visible until locations are actually loaded.
   * This is called from handleLocationStoreChange when locations are synced.
   *
   * Note: Static map placeholder is handled by map-canvas component
   * and removed only after Google Maps initializes.
   */
  private hideSkeletonLoader() {
    // Remove skeleton components
    document.querySelector("skeleton-locations-drawer")?.remove();
    document.querySelector("skeleton-cart-button")?.remove();
  }

  /**
   * Cleanup on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeMap?.();
    this.unsubscribeCart?.();
    window.removeEventListener("resize", this.handleResize);
  }

  /**
   * Retry data loading after an error
   *
   * @remarks
   * Delegates to the store's fetchLocations() action, which uses
   * the locationsUrl already set by the page-map.ts entrypoint.
   */
  private retryLoadLocations() {
    locationStore.getState().fetchLocations();
  }

  /**
   * Handle window resize
   */
  private handleResize() {
    // Reset to map view on desktop
    if (window.innerWidth >= 768) {
      this.mobileView = MobileView.MAP;
    }
  }

  /**
   * Toggle mobile view
   */
  private setMobileView(view: MobileViewType) {
    this.mobileView = view;
  }

  /**
   * Handle back to map event from drawer
   */
  private handleBackToMap() {
    this.setMobileView(MobileView.MAP);
  }

  /**
   * Handle open drawer event
   */
  private handleOpenDrawer() {
    this.setMobileView(MobileView.LIST);
  }

  /**
   * Toggle cart
   */
  private toggleCart() {
    uiStore.getState().toggleCart();
  }

  /**
   * Render error state
   */
  private renderError() {
    return html`
      <div class="flex items-center justify-center h-full p-8">
        <div class="max-w-md text-center">
          <div
            class="bg-error-soft border border-error rounded-lg p-6 shadow-sm"
          >
            <svg
              class="w-12 h-12 text-error-icon mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 class="text-error-text mb-2">
              Failed to Load Map
            </h2>
            <p class="text-sm text-error-text mb-4">${this.error}</p>
            <button
              @click=${this.retryLoadLocations}
              class="px-4 py-2 bg-error text-white rounded-lg hover:bg-error transition-colors font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render loading state
   */
  private renderLoading() {
    return html`
      <div class="flex items-center justify-center h-full">
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-4"
          ></div>
          <p class="text-lg text-muted">Loading locations...</p>
        </div>
      </div>
    `;
  }

  /**
   * Render the map canvas area with floating cart button
   */
  private renderMapCanvas() {
    const hasItems = this.cartItemCount > 0;

    return html`
      <main class="flex-1 relative h-full">
        <!-- Map Canvas -->
        <peach-map-canvas .apiKey=${this.apiKey}></peach-map-canvas>

        <!-- Floating Cart Button (Top Right) -->
        <button
          @click=${this.toggleCart}
          class="fixed top-4 right-4 z-30 bg-surface shadow-lg rounded-full p-3 hover:bg-surface-muted transition-colors group"
          aria-label="Open cart"
        >
          <!-- Shopping Cart Icon -->
          <svg
            class="w-6 h-6 text-default group-hover:text-brand"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>

          <!-- Badge (item count) -->
          ${hasItems
            ? html`
                <span
                  class="absolute -top-1 -right-1 bg-brand-accent text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                >
                  ${this.cartItemCount}
                </span>
              `
            : ""}
        </button>
      </main>
    `;
  }

  /**
   * Render main view: sidebar + map canvas
   */
  private renderView() {
    return html`
      <div class="theme-cool h-full flex flex-col md:flex-row overflow-hidden relative">
        <!-- Locations Drawer (overlays on mobile, sidebar on desktop) -->
        <peach-locations-drawer
          .mobileView=${this.mobileView}
          @back-to-map=${this.handleBackToMap}
          @open-drawer=${this.handleOpenDrawer}
        ></peach-locations-drawer>

        ${this.renderMapCanvas()}
      </div>
    `;
  }

  render() {
    if (this.error) {
      return this.renderError();
    }

    if (this.isLoading) {
      return this.renderLoading();
    }

    return this.renderView();
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-map-view": MapViewComponent;
  }
}
